import type { PatientFormValues } from '@/components/ui/CreateUpdatePatientModal'
import { states } from '@/utils/states'
import type { ZocdocAppointmentRow } from './AppointmentDetailsModal'

/** `fl` / `Florida` / `FL` → `FL`, or `''` when it matches no option the Select offers. */
function knownStateCode(value?: string): string {
  const wanted = value?.trim().toLowerCase()
  if (!wanted) return ''
  return (
    states.find(
      (option) => option.value.toLowerCase() === wanted || option.label.toLowerCase() === wanted,
    )?.value ?? ''
  )
}

export type ZocdocParticipantPatient = NonNullable<
  NonNullable<ZocdocAppointmentRow['participants']>['patient']
>

/**
 * Zocdoc's participant block → the create-patient form's initial values.
 *
 * A **prefill**, not a write: the operator sees every value in the form and submits it, which is
 * what makes mapping `sex_at_birth` onto the form's `gender` acceptable here. Zocdoc gives us no
 * administrative gender, the form requires one, and a value the operator can see and change is
 * better than an empty required field. The same logic in a silent server-side import would not be.
 *
 * Shared by the two places a chart is created from a booking — the details popup and the
 * appointments row — so both offer the operator the identical form.
 */
export function prefillFromZocdoc(patient: ZocdocParticipantPatient): Partial<PatientFormValues> {
  return {
    firstName: patient.firstName ?? '',
    lastName: patient.lastName ?? '',
    dob: patient.dateOfBirth ?? '',
    sexAtBirth: patient.sexAtBirth ?? '',
    gender: patient.sexAtBirth ?? '',
    email: patient.email ?? '',
    phone: patient.phone ?? '',
    addresses: [
      {
        // The form carries one street line; Zocdoc splits it in two.
        line: [patient.address1, patient.address2].filter(Boolean).join(', '),
        city: patient.city ?? '',
        // Only a code the Select can actually represent. A value outside its options renders as
        // an empty dropdown that still submits nothing, which reads as "the address didn't come
        // through" — better to leave it visibly unset for the operator to pick.
        state: knownStateCode(patient.state),
        postalCode: patient.zipCode ?? '',
        use: undefined,
      },
    ],
  }
}

/**
 * Whether a booking carries enough to open the create form at all.
 *
 * Name and date of birth are the form's own minimum, so without them the button would open a form
 * the operator has to fill from scratch anyway — and the patient it would create is one nothing on
 * this screen can vouch for.
 */
export function canCreateChartFrom(patient?: ZocdocParticipantPatient): boolean {
  return Boolean(patient?.firstName && patient.lastName && patient.dateOfBirth)
}
